import { useMemo, useState } from "react";
import useSWR from "swr";
import { ArrowUpDown, Search } from "lucide-react";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, CartesianGrid, ReferenceLine, Legend } from "recharts";
import { compute } from "@/lib/finance";
import { usd, num, pct, fmtDate, usdCompact } from "@/lib/format";
import { Card, SectionHeader, inputCls } from "@/components/common/Ornament";
import ShortWarning from "@/components/common/ShortWarning";
import LocationMap from "@/components/property/LocationMap";

const COLS = [
  ["property_name", "Property"], ["address", "Address"], ["sale_date", "Sale Date"], ["sale_price", "Sale Price"],
  ["rooms", "Rooms"], ["ppk", "Price / Key"], ["hotel_class", "Class / Brand"], ["cap_rate", "Cap Rate"], ["notes", "Notes / Source"],
];

function CompsTable({ rows, sort, setSort }) {
  const cell = (r, k) => {
    if (k === "sale_price") return usd(r.sale_price);
    if (k === "ppk") return usd(r.ppk);
    if (k === "rooms") return num(r.rooms);
    if (k === "cap_rate") return r.cap_rate !== null && r.cap_rate !== undefined ? pct(r.cap_rate) : "—";
    if (k === "sale_date") return fmtDate(r.sale_date);
    if (k === "notes") return [r.notes, r.source].filter(Boolean).join(" · ");
    return r[k];
  };
  return (
    <div className="overflow-x-auto rounded-lg border border-[#E2DCD2] bg-white" data-testid="comps-table">
      <table className="w-full text-sm min-w-[980px]">
        <thead className="bg-[#0F2E23] text-[#F9F6F0]">
          <tr>
            {COLS.map(([k, l]) => (
              <th key={k} className="text-left px-3 py-3 font-semibold text-xs uppercase tracking-wider">
                <button onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))} className="inline-flex items-center gap-1 hover:text-[#D4AF37]" data-testid={`comps-sort-${k}`}>
                  {l} <ArrowUpDown className={`h-3 w-3 ${sort.key === k ? "text-[#D4AF37]" : "opacity-50"}`} />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id} className="border-b border-[#E2DCD2] hover:bg-[#F9F6F0]" data-testid={`comps-row-${i}`}>
              {COLS.map(([k]) => (
                <td key={k} className={`px-3 py-3 align-top ${["sale_price", "ppk", "rooms", "cap_rate"].includes(k) ? "font-mono-num whitespace-nowrap" : ""} ${k === "property_name" ? "font-semibold text-[#0F2E23]" : "text-slate-700"}`}>
                  {k === "property_name" && r.is_sample && <span className="mb-1 inline-block rounded bg-amber-100 text-amber-800 text-[0.6rem] font-bold px-1.5 py-0.5 tracking-wider" data-testid={`comps-sample-badge-${i}`}>SAMPLE DATA – REPLACE</span>}
                  <div>{k === "property_name" ? r.property_name.replace("SAMPLE DATA – REPLACE · ", "") : cell(r, k)}</div>
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={COLS.length} className="px-3 py-10 text-center text-slate-500">No comparable sales match your filters.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PpkChart({ rows, subjectCost, subjectExit }) {
  const data = rows.filter((r) => r.sale_date && !Number.isNaN(Date.parse(r.sale_date))).map((r) => ({ t: Date.parse(r.sale_date), ppk: r.ppk, name: r.property_name.replace("SAMPLE DATA – REPLACE · ", "") }));
  const max = Math.max(subjectCost || 0, subjectExit || 0, ...data.map((d) => d.ppk), 1) * 1.15;
  return (
    <Card eyebrow="Over Time" title="Price per Key vs. Subject" testId="comps-price-chart">
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ left: 0, right: 16, top: 10 }}>
            <CartesianGrid stroke="#E2DCD2" strokeDasharray="3 3" />
            <XAxis type="number" dataKey="t" domain={["dataMin - 7776000000", "dataMax + 7776000000"]} tickFormatter={(t) => new Date(t).getFullYear()} tick={{ fontSize: 11, fill: "#64748B" }} name="Sale date" />
            <YAxis type="number" dataKey="ppk" domain={[0, Math.ceil(max / 50000) * 50000]} tickFormatter={usdCompact} tick={{ fontSize: 11, fill: "#64748B" }} width={64} name="Price / key" />
            <ZAxis range={[90, 90]} />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => payload?.[0] ? (
              <div className="bg-white border border-[#E2DCD2] rounded px-3 py-2 text-xs shadow"><strong>{payload[0].payload.name}</strong><br />{fmtDate(new Date(payload[0].payload.t).toISOString())} · {usd(payload[0].payload.ppk)}/key</div>
            ) : null} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Scatter name="Comparable sales" data={data} fill="#3E5C4E" />
            {subjectCost > 0 && <ReferenceLine y={subjectCost} stroke="#C59B27" strokeWidth={2} label={{ value: `Subject cost/key ${usdCompact(subjectCost)}`, position: "insideTopLeft", fontSize: 11, fill: "#9A7A1E" }} />}
            {subjectExit > 0 && <ReferenceLine y={subjectExit} stroke="#0F2E23" strokeDasharray="6 4" strokeWidth={2} label={{ value: `Projected exit/key ${usdCompact(subjectExit)}`, position: "insideBottomRight", fontSize: 11, fill: "#0F2E23" }} />}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export default function Comps() {
  const { data: comps = [] } = useSWR("/comps");
  const { data: c } = useSWR("/content");
  const { data: fin } = useSWR("/financials");
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("all");
  const [sort, setSort] = useState({ key: "sale_date", dir: "desc" });
  const subject = useMemo(() => (fin ? compute(fin.baseline) : null), [fin]);

  const classes = [...new Set(comps.map((x) => x.hotel_class).filter(Boolean))];
  const rows = useMemo(() => {
    const term = q.toLowerCase();
    const list = comps.map((x) => ({ ...x, ppk: x.rooms ? x.sale_price / x.rooms : 0 }))
      .filter((x) => (cls === "all" || x.hotel_class === cls) && (!term || `${x.property_name} ${x.address} ${x.notes} ${x.source}`.toLowerCase().includes(term)));
    const dir = sort.dir === "asc" ? 1 : -1;
    return list.sort((a, b) => {
      const av = a[sort.key] ?? "", bv = b[sort.key] ?? "";
      return (typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv))) * dir;
    });
  }, [comps, q, cls, sort]);

  return (
    <div className="space-y-8">
      <SectionHeader eyebrow="Market Evidence" title="Comparable Hotel Sales" testId="comps-header">Recent hotel transactions in New Orleans, entered by the sponsor. Click any column to sort.</SectionHeader>
      <ShortWarning testId="comps-short-warning" />
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search property, address, notes…" className={inputCls + " pl-9"} data-testid="comps-table-search-input" />
        </div>
        <select value={cls} onChange={(e) => setCls(e.target.value)} className={inputCls + " sm:w-64"} data-testid="comps-class-filter">
          <option value="all">All classes / brands</option>
          {classes.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      <CompsTable rows={rows} sort={sort} setSort={setSort} />
      <div className="grid gap-6 xl:grid-cols-2">
        <Card eyebrow="Map" title="Comps vs. Subject Property" testId="comps-map-card">
          {c && <LocationMap subject={c.map.subject} points={rows.map((r) => ({ name: r.property_name.replace("SAMPLE DATA – REPLACE · ", ""), lat: r.lat, lng: r.lng, description: `${usd(r.ppk)}/key · ${fmtDate(r.sale_date)}`, color: "#C59B27" }))} height={360} testId="comps-map" />}
        </Card>
        <PpkChart rows={rows} subjectCost={subject?.costPerKey} subjectExit={subject?.exitPerKey} />
      </div>
    </div>
  );
}
